import { Container, IDestroyOptions, Text, TextStyle } from "pixi.js";
import { MonField } from "../../../common/components/MonField";
import { PaymentProcessingType } from "../type/PaymentProcessingType";
import { GameFont } from "../../../common/GameFont";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { PaymentProcessingViewState } from "../type/PaymentProcessingViewState";
import { MonBuyContainer } from "./MonBuyContainer";
import { MonNotEnoughTokensContainer } from "./MonNotEnoughTokensContainer";
import { MonPaymentProcessingContainer } from "./MonPaymentProcessingContainer";
import { Event } from "event-system";
import { gsap } from "gsap";
import { EthL1PaymentProcessingPopupSceneOptions } from "../scenes/EthL1PaymentProcessingPopupSceneOptions";
import { Erc20PaymentProcessingResourcePackage } from "../resources/Erc20PaymentProcessingResourcePackage";
import { Web3Model } from "../../../../../web3/models/Web3Model";

export class MonBaseContent extends Container {
    private static readonly BUILDING_TEXT = "Building ";
    private static readonly ROLL_PACKAGE = "Roll Package ";
    private static readonly PRICE_TEXT = "Price: ";
    private static readonly WITHOUT_GAS_FEES_TEXT = "(without gas fees)";
    private static readonly BALANCE_TEXT = "In-Game Wallet MON Balance: ";
    private static readonly SWITCH_STATE_TIME = 0.5;

    private readonly switchStateAnimation: gsap.core.Timeline;

    private priceText: Text;
    private priceMonField: MonField;
    private gasFeesText: Text;
    private monBalanceText: Text;
    private balanceMonField: MonField;
    private currentStateContainer: Container;
    private currentViewState: PaymentProcessingViewState;

    public readonly onBuyClick: Event;

    public constructor(
        private readonly commonResources: CommonResourcesPackage,
        private readonly erc20Resources: Erc20PaymentProcessingResourcePackage,
        private readonly options: EthL1PaymentProcessingPopupSceneOptions<PaymentProcessingType>,
        private readonly web3Model: Web3Model
    ) {
        super();
        this.commonResources = commonResources;
        this.erc20Resources = erc20Resources;
        this.options = options;
        this.onBuyClick = new Event();
        this.switchStateAnimation = new gsap.core.Timeline({ paused: true });
        this.currentViewState = null;
        this.build();
        this.switchState(Number(this.web3Model.balanceMON.value) < options.price ? PaymentProcessingViewState.NOT_ENOUGH_TOKENS : PaymentProcessingViewState.BUY);
        this.layout();
    }

    private build(): void {
        const text = `${this.options.type === PaymentProcessingType.BUILDING ? MonBaseContent.BUILDING_TEXT : MonBaseContent.ROLL_PACKAGE}${MonBaseContent.PRICE_TEXT}`
        this.priceText = new Text(text, new DefaultTextStyle());
        this.priceMonField = new MonField(this.options.price, this.erc20Resources.assets_views_erc_20_payment_processing_mon_icon_png, new monTextStyle());
        this.gasFeesText = new Text(MonBaseContent.WITHOUT_GAS_FEES_TEXT, new DefaultTextStyle());
        this.monBalanceText = new Text(MonBaseContent.BALANCE_TEXT, new DefaultTextStyle());
        this.monBalanceText.style.letterSpacing = 3.3;
        this.balanceMonField = new MonField(Number(this.web3Model.balanceMON.value), this.erc20Resources.assets_views_erc_20_payment_processing_mon_icon_png, new monTextStyle());
        
        this.addChild(
            this.priceText,
            this.priceMonField,
            this.gasFeesText,
            this.monBalanceText,
            this.balanceMonField,
        );
    }

    public async switchState(toState: PaymentProcessingViewState): Promise<void> {
        if (this.currentViewState === toState) {
            return;
        }
        this.currentViewState = toState;

        await this.hideStateContainer();
        if (this.currentStateContainer != null) {
            if (this.currentStateContainer instanceof MonBuyContainer) {
                this.currentStateContainer.buyButtonClick.remove(this.onBuyButtonClick, this);
            }
            this.currentStateContainer.destroy();
            this.currentStateContainer = null;
        }

        switch (toState) {
            case PaymentProcessingViewState.BUY:
                const monBuyContainer = this.currentStateContainer = new MonBuyContainer(this.commonResources);
                monBuyContainer.buyButtonClick.add(this.onBuyButtonClick, this);
                monBuyContainer.position.set(this.width * 0.5 - monBuyContainer.width * 0.5, 343);
                break;
            case PaymentProcessingViewState.NOT_ENOUGH_TOKENS:
                this.currentStateContainer = new MonNotEnoughTokensContainer(this.erc20Resources, this.web3Model.smartWalletAddress ?? this.web3Model.address.value);
                this.currentStateContainer.position.set(this.width * 0.5 - this.currentStateContainer.width * 0.5, 307);
                break;
            case PaymentProcessingViewState.PAYMENT_PROCESSING:
                this.currentStateContainer = new MonPaymentProcessingContainer(this.commonResources);
                this.currentStateContainer.position.set(this.width * 0.5 - this.currentStateContainer.width * 0.5, 356);
                break;
        }
        this.addChild(this.currentStateContainer);
        await this.showStateContainer();
    }

    private onBuyButtonClick(): void {
        this.onBuyClick.emit();
    }

    private async hideStateContainer(): Promise<void> {
        if (this.currentStateContainer == null) {
            return;
        }
        this.switchStateAnimation.clear();
        this.switchStateAnimation.fromTo(this.currentStateContainer, { alpha: 1 }, { alpha: 0, ease: 'power1.out', duration: MonBaseContent.SWITCH_STATE_TIME * 0.5 });
        await this.switchStateAnimation.play();
    }

    private async showStateContainer(): Promise<void> {
        if (this.currentStateContainer == null) {
            return;
        }
        this.switchStateAnimation.clear();
        this.switchStateAnimation.fromTo(this.currentStateContainer, { alpha: 0 }, { alpha: 1, ease: 'power1.out', duration: MonBaseContent.SWITCH_STATE_TIME * 0.5 });
        await this.switchStateAnimation.play();
    }

    private layout(): void {
        this.monBalanceText.position.set(0, 138);
        this.balanceMonField.position.set(this.monBalanceText.x + this.monBalanceText.width, this.monBalanceText.y + 2);
        this.priceText.position.set(this.width * 0.5 - (this.priceText.width + this.priceMonField.width) * 0.5, 0);
        const monFieldPosOffset = this.options.price == 0 ? 10 : 0;
        this.priceMonField.position.set(this.priceText.x + this.priceText.width + monFieldPosOffset, this.priceText.y + 2);
        this.gasFeesText.position.set(this.priceText.x, this.priceText.y + this.priceText.height - 5);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.switchStateAnimation.kill();
        super.destroy(options);
    }
}

class monTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: [
                "#FFE302",
                "#FF9024"
            ],
            fillGradientStops: [0.2],
            fontSize: 40,
            align: "center",
            letterSpacing: 2.7
        });
    }
}

export class DefaultTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40,
            fill: '#ffffff',
            letterSpacing: 2.7
        });
    }
}
